import {useCallback, useMemo, useState} from 'react';
import {
  generatePath,
  generateRandomId,
  ImageValue,
  ImageWithCrop,
  MediaDetection,
  UserId
} from '@legit-health/ui';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import MenuIcon from '@mui/icons-material/Menu';
import SendIcon from '@mui/icons-material/Send';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  LinearProgress,
  Stack,
  Typography
} from '@mui/material';
import * as Sentry from '@sentry/react';
import {useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';
import {IMAGE_PROTOCOLS} from '@/shared/const/backend';
import useApiMutation from '@/shared/hooks/useApiMutation';
import {ImageProtocolId} from '@/shared/interfaces/ImageProtocol';
import {ImageProtocolCaptureResult} from '../../ImageProtocolCaptureContent';
import BodyRegionGuide from './components/BodyRegionGuide';
import RegionSidebar, {RegionStatus} from './components/RegionSidebar';
import RegionStep from './components/RegionStep';
import ReviewStep from './components/ReviewStep';
import useAihs4SeverityAssessment, {
  AihsScore,
  AihsSeverityAssessmentData
} from './hooks/useAihs4SeverityAssessment';
import {HS_PROTOCOL_REGIONS, ProtocolRegion} from './regions';
import apiDetectionToMediaDetection from './utils/apiDetectionToMediaDetection';
import buildSubmitPayload from './utils/buildSubmitPayload';

/**
 * Full per-slot state for the HS wizard.
 *
 * Phases:
 * - `empty`: user has not picked an image yet.
 * - `pending`: image picked but not yet sent to the AIHS4 endpoint.
 * - `sending`: the assessment request is in flight.
 * - `invalid`: the endpoint rejected the image; user must upload a new one.
 * - `assessed`: the endpoint accepted the image; detections are editable.
 */
export type SlotPhase = 'empty' | 'pending' | 'sending' | 'invalid' | 'assessed';

export interface SlotState {
  image: ImageValue;
  phase: SlotPhase;
  detections: MediaDetection[];
  score: AihsScore | null;
  errorMessage: string | null;
}

export type SlotStatesByKey = Record<string, SlotState>;

interface HidradenitisSuppurativaWizardProps {
  patientId: UserId;
  onComplete: (result: ImageProtocolCaptureResult) => void;
}

const REVIEW_STEP_INDEX = HS_PROTOCOL_REGIONS.length;

function buildInitialSlots(): SlotStatesByKey {
  const initial: SlotStatesByKey = {};
  HS_PROTOCOL_REGIONS.forEach(function (region) {
    initial[region.id] = {
      image: {crop: null, file: null, id: generateRandomId()},
      phase: 'empty',
      detections: [],
      score: null,
      errorMessage: null
    };
  });
  return initial;
}

function deriveInvalidMessage(
  assessment: AihsSeverityAssessmentData,
  t: ReturnType<typeof useTranslation>['t']
): string {
  const validity = assessment.mediaValidity;
  if (!validity) {
    return t('shared/imageProtocol:invalidImage');
  }
  if (!validity.domain.isDermatological) {
    return t('shared/imageProtocol:invalidImageReasonDomain');
  }
  if (!validity.quality.acceptable) {
    return t('shared/imageProtocol:invalidImageReasonQuality');
  }
  if (validity.modality.value === 'None') {
    return t('shared/imageProtocol:invalidImageReasonModality');
  }
  return t('shared/imageProtocol:invalidImage');
}

export default function HidradenitisSuppurativaWizard({
  patientId,
  onComplete
}: HidradenitisSuppurativaWizardProps) {
  const {t} = useTranslation([
    'practitioner/common',
    'shared/common',
    'shared/imageProtocol',
    'ui'
  ]);
  const [currentStep, setCurrentStep] = useState(0);
  const [skippedRegions, setSkippedRegions] = useState<string[]>([]);
  const [slotStates, setSlotStates] = useState<SlotStatesByKey>(buildInitialSlots);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const queryClient = useQueryClient();
  const {sendRequest} = useAihs4SeverityAssessment();
  const {mutateAsync: submitProtocol} = useApiMutation<{id: ImageProtocolId}>();

  const isReviewStep = currentStep === REVIEW_STEP_INDEX;
  const region = !isReviewStep ? (HS_PROTOCOL_REGIONS[currentStep] ?? null) : null;
  const isSkipped = region !== null ? skippedRegions.includes(region.id) : false;

  const getRegionAssessedCount = useCallback(
    function (regionId: string) {
      return slotStates[regionId]?.phase === 'assessed' ? 1 : 0;
    },
    [slotStates]
  );

  const getRegionStatus = useCallback(
    function (regionId: string): RegionStatus {
      if (skippedRegions.includes(regionId)) {
        return 'skipped';
      }
      return slotStates[regionId]?.phase === 'assessed' ? 'complete' : 'empty';
    },
    [slotStates, skippedRegions]
  );

  const totalSteps = REVIEW_STEP_INDEX + 1;
  const progress = useMemo(() => ((currentStep + 1) / totalSteps) * 100, [currentStep, totalSteps]);

  // Current region's slot state (one image per region).
  const currentSlotState = region ? slotStates[region.id] : null;
  const currentPhase = currentSlotState?.phase ?? 'empty';
  const isSending = currentPhase === 'sending';
  const hasPendingToSend = currentPhase === 'pending';
  const hasAnyAssessed = currentPhase === 'assessed';
  const canProceed = (currentPhase === 'empty' || currentPhase === 'assessed') && !isSending;

  function handleSelectStep(step: number) {
    setCurrentStep(step);
  }

  function handleNext() {
    setCurrentStep((prev) => Math.min(prev + 1, REVIEW_STEP_INDEX));
  }

  function handleBack() {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  }

  async function handleComplete() {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const payload = await buildSubmitPayload({patientId, slotStates, skippedRegions});
      const response = await submitProtocol({
        path: generatePath(IMAGE_PROTOCOLS, {}),
        method: 'POST',
        body: payload
      });
      await queryClient.invalidateQueries({queryKey: [IMAGE_PROTOCOLS]});
      onComplete({imageProtocolId: response.data.id});
    } catch (error) {
      Sentry.captureException(error);
      setSubmitError(
        error instanceof Error
          ? error.message
          : t('practitioner/common:diagnosticReport.submitError')
      );
      setIsSubmitting(false);
    }
  }

  function handleSkip() {
    if (!region) {
      return;
    }
    setSkippedRegions((prev) => (prev.includes(region.id) ? prev : [...prev, region.id]));
    handleNext();
  }

  function handleRetakeCurrentRegion() {
    if (!region) {
      return;
    }
    handleRetakeSlot(region.id);
  }

  const handleImageChange = useCallback(function (slotId: string, image: ImageWithCrop | null) {
    setSlotStates(function (prev) {
      const current = prev[slotId];
      const previousId = current?.image.id ?? generateRandomId();
      if (!image) {
        return {
          ...prev,
          [slotId]: {
            image: {crop: null, file: null, id: previousId},
            phase: 'empty',
            detections: [],
            score: null,
            errorMessage: null
          }
        };
      }
      return {
        ...prev,
        [slotId]: {
          image: {...image, id: previousId},
          phase: 'pending',
          detections: [],
          score: null,
          errorMessage: null
        }
      };
    });
  }, []);

  const handleRetakeSlot = useCallback(function (slotId: string) {
    setSlotStates(function (prev) {
      const current = prev[slotId];
      if (!current) {
        return prev;
      }
      return {
        ...prev,
        [slotId]: {
          image: {crop: null, file: null, id: generateRandomId()},
          phase: 'empty',
          detections: [],
          score: null,
          errorMessage: null
        }
      };
    });
  }, []);

  const handleDetectionsChange = useCallback(function (
    slotId: string,
    detections: MediaDetection[]
  ) {
    setSlotStates(function (prev) {
      const current = prev[slotId];
      if (!current) {
        return prev;
      }
      return {...prev, [slotId]: {...current, detections}};
    });
  }, []);

  async function sendSlot(region: ProtocolRegion, slotId: string) {
    const state = slotStates[slotId];
    if (!state?.image.file) {
      return;
    }
    setSlotStates((prev) => {
      const current = prev[slotId];
      if (!current) {
        return prev;
      }
      return {
        ...prev,
        [slotId]: {...current, phase: 'sending', errorMessage: null}
      };
    });
    try {
      const data = await sendRequest({
        file: state.image.file,
        bodySiteCode: region.bodySiteCode
      });
      setSlotStates((prev) => {
        const current = prev[slotId];
        if (!current) {
          return prev;
        }
        if (data.mediaValidity && data.mediaValidity.isValid) {
          return {
            ...prev,
            [slotId]: {
              ...current,
              phase: 'assessed',
              detections: data.patientEvolution.aihs4.media.detection.map(
                apiDetectionToMediaDetection
              ),
              score: data.patientEvolution.aihs4.score,
              errorMessage: null
            }
          };
        }
        // Invalid image — clear the picked file so the user can upload a new one
        return {
          ...prev,
          [slotId]: {
            image: {crop: null, file: null, id: generateRandomId()},
            phase: 'invalid',
            detections: [],
            score: null,
            errorMessage: deriveInvalidMessage(data, t)
          }
        };
      });
    } catch (error) {
      setSlotStates((prev) => {
        const current = prev[slotId];
        if (!current) {
          return prev;
        }
        return {
          ...prev,
          [slotId]: {
            ...current,
            phase: 'pending',
            errorMessage:
              error instanceof Error ? error.message : t('shared/imageProtocol:invalidImage')
          }
        };
      });
    }
  }

  async function handleSend() {
    if (!region) {
      return;
    }
    await sendSlot(region, region.id);
  }

  return (
    <Box sx={{display: 'flex', flexGrow: 1, minHeight: 0, position: 'relative'}}>
      <RegionSidebar
        regions={HS_PROTOCOL_REGIONS}
        currentStep={isReviewStep ? -1 : currentStep}
        getRegionStatus={getRegionStatus}
        onSelect={handleSelectStep}
        t={t}
      />

      {drawerOpen && (
        <Box
          onClick={() => setDrawerOpen(false)}
          sx={{
            display: {xs: 'block', lg: 'none'},
            position: 'absolute',
            inset: 0,
            bgcolor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 10
          }}
        />
      )}
      <Box
        sx={{
          display: {xs: 'flex', lg: 'none'},
          position: 'absolute',
          top: 0,
          left: 0,
          bottom: 0,
          width: 260,
          bgcolor: 'background.paper',
          boxShadow: 3,
          zIndex: 11,
          transform: drawerOpen ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 0.2s ease-in-out'
        }}
      >
        <RegionSidebar
          regions={HS_PROTOCOL_REGIONS}
          currentStep={isReviewStep ? -1 : currentStep}
          getRegionStatus={getRegionStatus}
          onSelect={(step) => {
            handleSelectStep(step);
            setDrawerOpen(false);
          }}
          t={t}
          variant="drawer"
        />
      </Box>

      <Box sx={{flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0}}>
        {/* ── Header ─────────────────────────────────────────────── */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{
            px: {xs: 2, sm: 3},
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}
        >
          <IconButton
            size="small"
            onClick={() => setDrawerOpen(true)}
            sx={{display: {xs: 'inline-flex', lg: 'none'}}}
            aria-label="open regions list"
          >
            <MenuIcon />
          </IconButton>

          {region ? (
            <>
              <Box
                sx={{
                  width: {xs: 28, sm: 32},
                  height: {xs: 28, sm: 32},
                  borderRadius: 1,
                  bgcolor: 'primary.light',
                  color: 'primary.contrastText',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  fontWeight: 700,
                  fontSize: {xs: '0.8rem', sm: '0.875rem'}
                }}
              >
                {region.number}
              </Box>
              <Typography
                variant="h6"
                component="h1"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                  minWidth: 0,
                  fontSize: {xs: '1rem', sm: '1.25rem'}
                }}
              >
                {region.name}
              </Typography>
            </>
          ) : (
            <Typography
              variant="h6"
              component="h1"
              sx={{flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis'}}
            >
              {t('shared/imageProtocol:review')}
            </Typography>
          )}
        </Stack>

        <LinearProgress variant="determinate" value={progress} sx={{height: 4}} />

        {/* ── Content ────────────────────────────────────────────── */}
        <Box sx={{flex: 1, overflowY: 'auto', px: {xs: 2, sm: 3}, py: {xs: 2, sm: 3}}}>
          <Box sx={{maxWidth: 1200, mx: 'auto'}}>
            {region ? (
              <>
                {/* Body guide — compact strip above on mobile, hidden on lg (shown in sidebar column) */}
                <Box sx={{display: {xs: 'block', lg: 'none'}, mb: 2}}>
                  <BodyRegionGuide
                    currentRegionId={region.id}
                    getRegionStatus={getRegionStatus}
                    onSelectRegion={(id) => {
                      const idx = HS_PROTOCOL_REGIONS.findIndex((r) => r.id === id);
                      if (idx >= 0) {
                        handleSelectStep(idx);
                      }
                    }}
                  />
                </Box>
                <Stack direction="row" spacing={3} alignItems="flex-start">
                  <Box sx={{flex: 1, minWidth: 0, width: '100%'}}>
                    <RegionStep
                      region={region}
                      isSkipped={isSkipped}
                      slotStates={slotStates}
                      onImageChange={handleImageChange}
                      onDetectionsChange={handleDetectionsChange}
                      t={t}
                    />
                  </Box>
                  {/* Body guide — sticky sidebar on desktop */}
                  <Box
                    sx={{
                      display: {xs: 'none', lg: 'block'},
                      width: 280,
                      flexShrink: 0,
                      position: 'sticky',
                      top: 0
                    }}
                  >
                    <BodyRegionGuide
                      currentRegionId={region.id}
                      getRegionStatus={getRegionStatus}
                      onSelectRegion={(id) => {
                        const idx = HS_PROTOCOL_REGIONS.findIndex((r) => r.id === id);
                        if (idx >= 0) {
                          handleSelectStep(idx);
                        }
                      }}
                    />
                  </Box>
                </Stack>
              </>
            ) : (
              <>
                {submitError && (
                  <Alert
                    icon={<ErrorOutlineIcon />}
                    severity="error"
                    sx={{mb: 2}}
                    onClose={() => setSubmitError(null)}
                  >
                    {submitError}
                  </Alert>
                )}
                <ReviewStep
                  regions={HS_PROTOCOL_REGIONS}
                  getRegionStatus={getRegionStatus}
                  getRegionCapturedCount={getRegionAssessedCount}
                  onEditRegion={handleSelectStep}
                  t={t}
                />
              </>
            )}
          </Box>
        </Box>

        {/* ── Footer ─────────────────────────────────────────────── */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            px: {xs: 1, sm: 3},
            py: {xs: 1, sm: 1.5},
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            gap: 0.5
          }}
        >
          <Button
            size="small"
            disabled={currentStep === 0 || isSending}
            onClick={handleBack}
            startIcon={<ArrowBackIosIcon />}
            sx={{minWidth: 0, px: {xs: 1, sm: 2}}}
          >
            <Box component="span" sx={{display: {xs: 'none', sm: 'inline'}}}>
              {t('ui:previous')}
            </Box>
          </Button>

          {isReviewStep ? (
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={handleComplete}
              disabled={isSubmitting}
              startIcon={
                isSubmitting ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />
              }
              data-testid="completeImageProtocolButton"
            >
              {t('shared/imageProtocol:completeProtocol')}
            </Button>
          ) : (
            <Stack direction="row" spacing={0.5} sx={{flexShrink: 0}}>
              {hasAnyAssessed ? (
                <>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleRetakeCurrentRegion}
                    startIcon={<CameraAltIcon />}
                    sx={{minWidth: 0, px: {xs: 1, sm: 2}}}
                  >
                    <Box component="span" sx={{display: {xs: 'none', sm: 'inline'}}}>
                      {t('shared/imageProtocol:retake')}
                    </Box>
                  </Button>

                  {hasPendingToSend && (
                    <Button
                      size="small"
                      variant="contained"
                      color="secondary"
                      onClick={handleSend}
                      disabled={isSending}
                      startIcon={
                        isSending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />
                      }
                      data-testid="sendImageProtocolButton"
                    >
                      {t('shared/imageProtocol:send')}
                    </Button>
                  )}

                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={handleNext}
                    disabled={!canProceed}
                    endIcon={<ArrowForwardIosIcon />}
                  >
                    {currentStep === REVIEW_STEP_INDEX - 1
                      ? t('shared/imageProtocol:review')
                      : t('shared/imageProtocol:nextRegion')}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleSkip}
                    color="inherit"
                    disabled={isSending}
                    startIcon={<SkipNextIcon />}
                    sx={{minWidth: 0, px: {xs: 1, sm: 2}}}
                  >
                    <Box component="span" sx={{display: {xs: 'none', sm: 'inline'}}}>
                      {t('shared/imageProtocol:skip')}
                    </Box>
                  </Button>

                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={handleSend}
                    disabled={!hasPendingToSend || isSending}
                    startIcon={
                      isSending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />
                    }
                    data-testid="sendImageProtocolButton"
                  >
                    {t('shared/imageProtocol:send')}
                  </Button>
                </>
              )}
            </Stack>
          )}
        </Stack>
      </Box>
    </Box>
  );
}
